import {
    Dialog,
    DialogType,
    DialogFooter,
    PrimaryButton,
    DefaultButton,
    Label,
} from '@fluentui/react'
import React, { useEffect, useState } from 'react'
import OrderQuote from 'requests/objects/order-quote'
import formatPrice from 'helpers/methods/formatPrice'
import useTranslate from 'helpers/hooks/useTranslate'
import DatePicker from 'components/inputs/datePicker'

const DateFilterMinDate = new Date(1970, 0, 1)
const DateFilterMaxDate = new Date(2100, 11, 31)

/**
 * Modal allowing admin to define and confirm the expiration date
 * before sending the costing order quote to the client.
 *
 * Displays a summary of the quote (services + totals)
 * and allows selecting an expiration date.
 *
 * @param {object} props Component props
 * @param {boolean} props.isVisible Indicates whether the modal is displayed
 * @param {(visible: boolean) => void} props.setIsVisible Function to control modal visibility
 * @param {Date|null} [props.initialDate] Initial expiration date (can be null)
 * @param {OrderQuote} props.item Current order quote entity
 * @param {string} props.lang Current application language
 * @param {(date: Date|null) => void} props.onConfirm Callback triggered when user confirms the expiration date
 * @returns {JSX.Element|null} The modal component or null if not visible
 */
export default function ConfirmationModal({
    isVisible,
    setIsVisible,
    initialDate,
    item,
    lang,
    onConfirm,
}) {
    const { tKey } = useTranslate({ lang })

    const [expirationDate, setExpirationDate] = useState(initialDate || null)

    useEffect(() => {
        setExpirationDate(initialDate || null)
    }, [initialDate])

    if (!isVisible)
        return null

    return (
        <Dialog
            hidden={!isVisible}
            onDismiss={() => setIsVisible(false)}
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: tKey('sendCostingOrderQuote'),
            }}
            modalProps={{ isBlocking: true }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <span>
                    {tKey('validationOfTheCommercialProposal', {
                        price: formatPrice(OrderQuote.getTotal(item.orderQuotePricings)),
                        totalPrice: formatPrice(OrderQuote.getTotalPrice(item.orderQuotePricings)),
                    })}
                </span>

                <span>
                    {tKey('numberOfServiceLines', { nb: item.orderQuotePricings.length.toString() })}
                </span>

                <span>{tKey('listOfServiceLines')}</span>

                <ul>
                    {item.orderQuotePricings.map(p => (
                        <li key={p.orderQuotePricingId}>
                            <b>{p.orderQuoteService.name}</b>
                        </li>
                    ))}
                </ul>

                <div>
                    <Label>{`${tKey('totalHtEstimated')} :`}</Label>
                    <span>{formatPrice(OrderQuote.getTotal(item.orderQuotePricings))}</span>
                </div>

                <div>
                    <Label>{`${tKey('totalPriceEstimated')} :`}</Label>
                    <span>{formatPrice(OrderQuote.getTotalPrice(item.orderQuotePricings))}</span>
                </div>

                <DatePicker
                    label={tKey('expirationDateLabel')} // si la clé existe
                    placeholder={tKey('expirationDateDefault')} // si la clé existe
                    date={expirationDate}
                    minDate={DateFilterMinDate}
                    maxDate={DateFilterMaxDate}
                    lang={lang}
                    onChangeDate={date => setExpirationDate(date || null)}
                />
            </div>

            <DialogFooter>
                <PrimaryButton
                    onClick={() => {
                        onConfirm(expirationDate)
                        setIsVisible(false)
                    }}
                    text={tKey('validate')}
                />
                <DefaultButton
                    onClick={() => setIsVisible(false)}
                    text={tKey('cancel')}
                />
            </DialogFooter>
        </Dialog>
    )
}
